/**
 * useAddressAutocomplete Hook
 * Handles address autocomplete using Nominatim API (OSM)
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { AddressSuggestion } from "./types";

export interface UseAddressAutocompleteReturn {
  addressQuery: string;
  setAddressQuery: (query: string) => void;
  addressSuggestions: AddressSuggestion[];
  showSuggestions: boolean;
  setShowSuggestions: (show: boolean) => void;
  addressLoading: boolean;
  locatingAddress: boolean;
  selectAddress: (suggestion: AddressSuggestion) => string;
  handleUseMyLocation: () => Promise<string | null>;
}

// Florida bounding box
const FL_BOUNDS = {
  west: -87.6349,
  north: 31.0009,
  east: -79.9743,
  south: 24.3963,
};

export function useAddressAutocomplete(
  onAddressSelect?: (address: string) => void
): UseAddressAutocompleteReturn {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const lastQueryRef = useRef<string>("");

  const fetchSuggestions = useCallback(async (searchQuery: string): Promise<void> => {
    const trimmed = searchQuery.trim();
    if (!trimmed || trimmed.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Cancel any in-flight request
      abortRef.current?.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;
      lastQueryRef.current = trimmed;

      const params = new URLSearchParams({
        format: "jsonv2",
        q: trimmed,
        countrycodes: "us",
        layer: "address",
        limit: "20",
        addressdetails: "1",
        "accept-language": "en-US",
        viewbox: `${FL_BOUNDS.west},${FL_BOUNDS.north},${FL_BOUNDS.east},${FL_BOUNDS.south}`,
        bounded: "1",
        dedupe: "1",
      });

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?${params.toString()}`,
        {
          headers: { "User-Agent": "WIHMedia-Booking-App" },
          signal: abortController.signal,
        }
      );

      if (!response.ok) throw new Error("Failed to fetch address suggestions");

      const data = await response.json();

      // Ignore responses for stale queries
      if (abortController.signal.aborted || lastQueryRef.current !== trimmed) {
        return;
      }

      const isInFlorida = (item: any): boolean => {
        const addr = item?.address || {};
        const stateRaw = String(addr.state ?? "").toLowerCase();
        const iso = String(addr["ISO3166-2-lvl4"] ?? "").toLowerCase();
        if (stateRaw.includes("florida") || stateRaw === "fl") return true;
        if (iso.includes("us-fl")) return true;

        const lat = Number.parseFloat(item?.lat);
        const lon = Number.parseFloat(item?.lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return false;
        return (
          lon >= FL_BOUNDS.west &&
          lon <= FL_BOUNDS.east &&
          lat >= FL_BOUNDS.south &&
          lat <= FL_BOUNDS.north
        );
      };

      const scoreResult = (item: any): number => {
        const addr = item?.address || {};
        let score = 0;

        if (addr.house_number && addr.road) score += 60;
        if (addr.road) score += 10;
        if (addr.postcode) score += 8;
        if (addr.city || addr.town || addr.village) score += 12;
        if (addr.suburb || addr.neighbourhood) score += 4;

        const type = String(item?.type ?? "").toLowerCase();
        const klass = String(item?.class ?? "").toLowerCase();
        if (["house", "residential", "building"].includes(type)) score += 12;
        if (klass === "building") score += 10;

        const q = trimmed.toLowerCase();
        const dn = String(item?.display_name ?? "").toLowerCase();
        if (dn.startsWith(q)) score += 15;
        const qTokens = q.split(/\s+/).filter(Boolean);
        const matches = qTokens.reduce((acc, tok) => acc + (dn.includes(tok) ? 1 : 0), 0);
        score += matches * 2;

        const importance = Number(item?.importance);
        if (Number.isFinite(importance)) score += Math.min(importance, 1) * 10;

        return score;
      };

      const floridaOnly = Array.isArray(data) ? data.filter(isInFlorida) : [];
      const ranked = floridaOnly.slice().sort((a: any, b: any) => {
        const diff = scoreResult(b) - scoreResult(a);
        if (diff !== 0) return diff;
        return Number(b?.place_id ?? 0) - Number(a?.place_id ?? 0);
      });

      const best = ranked.slice(0, 8);
      const formatted: AddressSuggestion[] = best.map((item: any) => {
        const addr = item.address || {};
        let mainText = "";
        let secondaryText = "";

        if (addr.house_number && addr.road) {
          mainText = `${addr.house_number} ${addr.road}`;
          const parts = [
            addr.neighbourhood || addr.suburb,
            addr.city || addr.town || addr.village,
            addr.state,
            addr.postcode,
          ].filter(Boolean);
          secondaryText = parts.join(", ");
        } else {
          const parts = item.display_name.split(",").map((s: string) => s.trim());
          mainText = parts[0] || "";
          secondaryText = parts.slice(1).join(", ");
        }

        return {
          placeId: item.place_id.toString(),
          description: item.display_name,
          mainText,
          secondaryText,
        };
      });

      setSuggestions(formatted);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      console.error("Error fetching address suggestions:", error);
      setSuggestions([]);
    } finally {
      if (lastQueryRef.current === searchQuery.trim()) {
        setLoading(false);
      }
    }
  }, []);

  // Debounce address lookup
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query.length >= 3) {
        fetchSuggestions(query);
      } else {
        setSuggestions([]);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [query, fetchSuggestions]);

  const selectAddress = useCallback(
    (suggestion: AddressSuggestion): string => {
      setQuery(suggestion.description);
      setShowSuggestions(false);
      onAddressSelect?.(suggestion.description);
      return suggestion.description;
    },
    [onAddressSelect]
  );

  const useMyLocation = useCallback(async (): Promise<string | null> => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return null;
    }

    return new Promise((resolve) => {
      setLocating(true);

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          try {
            const reverseAbort = new AbortController();
            const reverseTimeout = window.setTimeout(() => {
              reverseAbort.abort();
            }, 6000);

            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
              {
                headers: { "User-Agent": "WIHMedia-Booking-App" },
                signal: reverseAbort.signal,
              }
            );

            window.clearTimeout(reverseTimeout);

            if (!response.ok) throw new Error("Failed to get address from location");

            const data = await response.json();
            const address = data.display_name;

            setQuery(address);
            setShowSuggestions(false);
            onAddressSelect?.(address);
            resolve(address);
          } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") {
              alert("Getting your address took too long. Please try again.");
            } else {
              console.error("Error reverse geocoding:", error);
              alert("Failed to get address from your location. Please try again.");
            }
            resolve(null);
          } finally {
            setLocating(false);
          }
        },
        (error) => {
          console.error("Geolocation error:", error);
          let message = "Failed to get your location.";
          if (error.code === error.PERMISSION_DENIED) {
            message = "Location access was denied. Please enable location permissions.";
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            message = "Location information is unavailable.";
          } else if (error.code === error.TIMEOUT) {
            message = "Location request timed out.";
          }
          alert(message);
          setLocating(false);
          resolve(null);
        },
        {
          enableHighAccuracy: false,
          timeout: 8000,
          maximumAge: 5 * 60 * 1000,
        }
      );
    });
  }, [onAddressSelect]);

  return {
    addressQuery: query,
    setAddressQuery: setQuery,
    addressSuggestions: suggestions,
    showSuggestions,
    setShowSuggestions,
    addressLoading: loading,
    locatingAddress: locating,
    selectAddress,
    handleUseMyLocation: useMyLocation,
  };
}
